/* eslint-disable import/no-import-module-exports */
/**
 * app.js
 *
 * This is the entry file for the application, only setup and boilerplate
 * code.
 */
// Needed for promises es6 generator support
import 'core-js/stable';
import 'regenerator-runtime/runtime';
import './styles/master-styles.scss';

import App from 'components/App';
import React, {
    StrictMode, useEffect, useMemo, useState,
} from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { Provider } from 'react-redux';
import finalStore from 'store';
import PropTypes from 'prop-types';
import { ThemeContext } from 'utils/context';
import { isMemberDashboard as getIsMemberDashboard } from 'utils/is-member-dashboard';

let root = null;

// REMOVE THIS WHEN YOU ADD BUGSNAG
function ErrorBoundary({ children })
{
    return <> {children}</>;
}
ErrorBoundary.propTypes = {
    children: PropTypes.any,
};

function AnalyticsProvider({ children })
{
    function findTitleWithinParent(element)
    {
        let currentElement = element;
        while (currentElement)
        {
            const currentAttribute = currentElement.dataset.sectionTitle;
            if (currentAttribute)
            {
                return currentAttribute;
            }

            const titleElement = currentElement.querySelector('.tw-font-title');
            if (titleElement)
            {
                const titleAttribute = titleElement?.dataset.sectionTitle;
                if (titleAttribute)
                {
                    return titleAttribute;
                }
                return titleElement.textContent;
            }
            currentElement = currentElement.parentElement;
        }
        return null;
    }

    const handleClickFunction = (event) =>
    {
        const dataLayer = window?.dataLayer || null;

        if (!dataLayer)
        {
            return;
        }

        const button = event.target.closest('button, a');
        if (!button)
        {
            return;
        }

        const buttonText = button.dataset.displayLabel ?? button.textContent;
        const sectionTitle = findTitleWithinParent(button);

        if (buttonText)
        {
            dataLayer.push({ event: 'ClickEvent', mdb_text: buttonText?.trim(), mdb_section: sectionTitle?.trim() });
        }
    };

    useEffect(() =>
    {
        const wrapper = document.getElementById('ga4');
        wrapper.addEventListener('click', handleClickFunction);
        return () =>
        {
            wrapper.removeEventListener('click', handleClickFunction);
        };
    }, []);

    return (
        <div id="ga4">
            {children}
        </div>
    );
}

AnalyticsProvider.propTypes = {
    children: PropTypes.node,
};

function ThemeProvider({ children })
{
    const isMemberDashboard = getIsMemberDashboard();
    const themeProvider = isMemberDashboard ? 'theme-provider' : 'theme-provider-luxuria';
    const currentThemeModeKey = isMemberDashboard ? 'ujvCurrentThemeMode' : 'luxuriaCurrentThemeMode';
    const [darkMode, setDarkMode] = useState(localStorage.getItem(currentThemeModeKey) ?? window?.matchMedia('(prefers-color-scheme: dark)').matches ? true : '');
    const [theme, setTheme] = useState(localStorage.getItem(currentThemeModeKey) ?? window?.location.hostname.replaceAll('.', '-'));

    const value = useMemo(() => ({
        theme, setTheme, darkMode, setDarkMode,
    }), [theme, darkMode]);

    useEffect(() =>
    {
        localStorage?.setItem(currentThemeModeKey, theme);
    }, [theme]);

    useEffect(() =>
    {
        localStorage?.setItem(currentThemeModeKey, darkMode);
    }, [darkMode]);

    return (
        <ThemeContext.Provider value={value}>
            <div id="theme-provider" className={`${themeProvider} theme-${theme} ${darkMode ? 'tw-dark' : ''}`}>
                {children}
            </div>
        </ThemeContext.Provider>
    );
}

ThemeProvider.propTypes = {
    children: PropTypes.node,
};

function Final()
{
    return (
        <ErrorBoundary>
            <Provider store={finalStore}>
                <BrowserRouter>
                    <AnalyticsProvider>
                        <ThemeProvider>
                            <StrictMode>
                                <App />
                            </StrictMode>
                        </ThemeProvider>
                    </AnalyticsProvider>
                </BrowserRouter>
            </Provider>
        </ErrorBoundary>
    );
}

const render = () =>
{
    const container = document.getElementById('react-app');
    root = createRoot(container);

    root.render(<Final />);
};

if (module.hot)
{
    module.hot.accept(['components/App'], () =>
    {
        root.render(<Final />);
    });
}

render();
